import { ApiError, type Session } from '@outpost/api-client';

/** Where the refresh token lives, shared by every tab on the origin. */
export interface RefreshTokenStore {
  get(): string | null;
  remove(): void;
}

export type RefreshOutcome =
  | { kind: 'refreshed'; session: Session }
  /** There is no token, or the server refused the latest one. */
  | { kind: 'signed-out' }
  /** The server could not be asked. The token may still be good, so it is kept. */
  | { kind: 'unavailable' };

/**
 * Exchange the stored refresh token for a new session.
 *
 * The token is read from the store at the moment it is needed, never
 * remembered: refresh tokens rotate on every use, so a copy a tab took when it
 * loaded is revoked as soon as any other tab refreshes. For the same reason a
 * refusal is only final when the store still holds the token that was refused —
 * if another tab rotated it while this request was in flight, the newer one is
 * tried instead, and the store is left alone rather than signing out every tab
 * over a token nobody holds any more.
 */
export async function refreshStoredSession(
  store: RefreshTokenStore,
  refresh: (token: string) => Promise<Session>,
): Promise<RefreshOutcome> {
  let token = store.get();
  while (token !== null) {
    try {
      return { kind: 'refreshed', session: await refresh(token) };
    } catch (error) {
      // A network failure or an outage says nothing about the token.
      if (!(error instanceof ApiError && error.isAuthError)) return { kind: 'unavailable' };
      const stored = store.get();
      if (stored === token) {
        store.remove();
        return { kind: 'signed-out' };
      }
      token = stored;
    }
  }
  return { kind: 'signed-out' };
}
